import {
  Button,
  Flex,
  FormContainer,
  IconButton,
  useConfirmation,
} from '@wyz/ui';
import { invariant, useModalState } from '@wyz/utils';
import { isNil, pick } from 'lodash-es';
import { useForm, useWatch } from 'react-hook-form';
import { Trans, useI18n, usePlugin } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { useManageQuoteCart, useManageSearch } from '~/purchase/api';
import {
  QuoteDetailModal,
  QuoteProductCard,
  QuoteQuantitySelector,
} from '~/purchase/components';
import { QuoteHelpers } from '~/purchase/helpers';
import { SEARCH_TYPE_ENUM } from '~/purchase/schemas';
import { IQuoteTyreOffer } from '~/purchase/types';

type Props = {
  tyreOffer: IQuoteTyreOffer;
  showBuyingInfo?: boolean;
  defaultQuantity: number;
  customer: IUserCustomer;
  showBfDiscount: boolean;
  onRefreshTyre: (ean: string, quantity: number) => void;
};

function ProductResult({
  tyreOffer,
  defaultQuantity,
  showBuyingInfo = false,
  customer,
  showBfDiscount,
  onRefreshTyre,
}: Props) {
  const { t } = useI18n();
  const ask = useConfirmation();
  const methods = useForm<{ quantity: number }>({
    defaultValues: { quantity: defaultQuantity },
  });
  const manageCart = useManageQuoteCart({
    get: { enabled: true },
    addItem: { enabled: true },
    reset: { enabled: true },
    removeItem: { enabled: true },
  });

  const [modal, modalHandlers] = useModalState<'DETAIL'>();
  const quoteItemInfo = QuoteHelpers.getItemInfoFromTyreOffer(tyreOffer);

  const handleAddToCart = (quantity: number) => {
    invariant(manageCart.get.isSuccess);
    const cart = manageCart.get.data.data;

    if (
      cart.dimensionCount === QuoteHelpers.QUOTE_CART_MAX_DIMENSION_COUNT ||
      cart.referenceCount >= QuoteHelpers.QUOTE_CART_MAX_REFERENCE_COUNT ||
      (cart.referenceCount > 1 &&
        !cart.items.some((item) =>
          QuoteHelpers.isSameDimension(item, quoteItemInfo.tyre),
        ))
    ) {
      ask({
        title: t('app.purchase.quote_cart_page.messages.cannot_add_dimension'),
        confirmBtnText: t('app.purchase.cart_page.reset_cart'),
        cancelBtnText: t('app.common.button.cancel'),
      }).onConfirm(() => manageCart.reset.mutate());
      return;
    } else if (
      cart.dimensionCount === 1 &&
      !cart.items.some((item) =>
        QuoteHelpers.isSameDimension(item, quoteItemInfo.tyre),
      )
    ) {
      ask({
        title: t(
          'app.purchase.quote_cart_page.messages.different_dimension.title',
        ),
        description: t(
          'app.purchase.quote_cart_page.messages.different_dimension.description',
        ),
        confirmBtnText: t('app.purchase.cart_page.reset_cart'),
        cancelBtnText: t('app.purchase.quote_search_page.add_to_quote'),
      })
        .onConfirm(() => manageCart.reset.mutate())
        .onCancel(() => {
          invariant(quoteItemInfo.pricing);

          manageCart.addItem.mutate({
            ...pick(quoteItemInfo, ['tyre', 'stocks']),
            buyingPrice: quoteItemInfo.pricing.buyingPrice,
            unitPriceTaxIncl: quoteItemInfo.pricing.unitPriceTaxIncl,
            quantity,
            currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
          });
        });
    } else {
      invariant(quoteItemInfo.pricing);
      manageCart.addItem.mutate({
        ...pick(quoteItemInfo, ['tyre', 'stocks']),
        buyingPrice: quoteItemInfo.pricing.buyingPrice,
        unitPriceTaxIncl: quoteItemInfo.pricing.unitPriceTaxIncl,
        recyclingFee: quoteItemInfo.pricing.recyclingFee,
        quantity,
        currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
      });
    }
  };

  const cartItem = manageCart.get.data?.data.items.find(
    (cartItem) => cartItem.tyre.ean === quoteItemInfo.tyre.ean,
  );

  const quantityName = 'quantity';
  const watchedQuantity = useWatch({
    name: quantityName,
    control: methods.control,
  });
  const pluginContext = usePlugin();
  const searchContext = {
    customerId: customer.id,
    searchType: SEARCH_TYPE_ENUM.QUOTE,
  };

  const manageSearch = useManageSearch<IQuoteTyreOffer>({
    context: searchContext,
    refreshEan: { enabled: true },
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={({ quantity }) => {
        pluginContext.api.onAddToCart?.();
        handleAddToCart(quantity);
      }}
      className='position-relative'
    >
      <QuoteProductCard
        item={quoteItemInfo}
        onShowDetails={() => modalHandlers.open('DETAIL')}
        buyingInfo={
          quoteItemInfo.pricing
            ? pick(quoteItemInfo.pricing, ['buyingPrice', 'margin'])
            : undefined
        }
        showBuyingInfo={showBuyingInfo}
        requestedQuantity={watchedQuantity}
        customer={customer}
        showBfDiscount={showBfDiscount}
        currency={tyreOffer.tyre.priceFrame.currencyIsoCode}
      >
        {quoteItemInfo.pricing && (
          <div className='my-2'>
            <QuoteQuantitySelector
              name={quantityName}
              price={quoteItemInfo.pricing.unitPriceTaxIncl}
              currency={tyreOffer.tyre.priceFrame.currencyIsoCode}
              onChange={(quantity) =>
                onRefreshTyre(tyreOffer.tyre.ean, quantity)
              }
            />
          </div>
        )}
        <Flex className='gap-1'>
          {quoteItemInfo.pricing && (
            <Button
              id={`add-to-quote-${quoteItemInfo.tyre.ean}`}
              type='submit'
              variant='primary'
              fullWidth
              isLoading={manageCart.addItem.isLoading}
              disabled={!isNil(cartItem)}
            >
              <Trans i18nKey='app.purchase.quote_search_page.add_to_quote' />
            </Button>
          )}
          {!isNil(cartItem) && (
            <IconButton
              id={`remove-from-cart-${quoteItemInfo.tyre.ean}`}
              icon='trash'
              iconSize='sm'
              iconAlign='right'
              variant='tertiary-grey'
              onClick={() => manageCart.removeItem.mutate(cartItem.id)}
              isLoading={manageCart.removeItem.isLoading}
            >
              <Trans i18nKey='app.purchase.quote_search_page.product.delete' />
            </IconButton>
          )}
        </Flex>
      </QuoteProductCard>
      {modal === 'DETAIL' && (
        <QuoteDetailModal
          customer={customer}
          item={quoteItemInfo}
          onClose={() => modalHandlers.close()}
          currency={tyreOffer.tyre.priceFrame.currencyIsoCode}
        />
      )}
      {manageSearch.refreshEan.isLoading && <div className='shimmer-overlay' />}
    </FormContainer>
  );
}
export default ProductResult;
